import { CartUpdatedEvent } from '@theme/events';

const STORAGE_KEY_END = 'fullstack:cart-reservation-timer:end-timestamp';
const STORAGE_KEY_SIGNATURE = 'fullstack:cart-reservation-timer:cart-signature';
const STORAGE_KEY_EXPIRED = 'fullstack:cart-reservation-timer:expired';

class CartReservationTimer extends HTMLElement {
  constructor() {
    super();

    this.timeEl = null;
    this.activeEl = null;
    this.emptyEl = null;
    this.expiredEl = null;
    this.intervalId = null;
    this.endTimestamp = null;
    this.durationMs = 0;
    this.cartSignature = '';
    this.clearCartOnExpire = true;
    this.isClearingCart = false;

    this.storageHandler = this.#handleStorageChange.bind(this);
  }

  connectedCallback() {
    this.activeEl = this.querySelector('[data-ref="active"]');
    this.emptyEl = this.querySelector('[data-ref="empty"]');
    this.expiredEl = this.querySelector('[data-ref="expired"]');
    this.timeEl = this.querySelector('[data-ref="time"]');

    this.cartSignature = this.dataset.cartSignature || '';
    const durationMinutes = parseInt(this.dataset.durationMinutes, 10) || 15;
    this.durationMs = durationMinutes * 60 * 1000;
    this.clearCartOnExpire = this.dataset.clearCartOnExpire !== 'false';
    this.isDesignMode = window.Shopify?.designMode === true;

    if (!this.cartSignature) {
      if (this.#isExpired()) this.#showExpired();
      else this.#showEmpty();
      return;
    }

    this.#initTimer();

    if (!this.isDesignMode) window.addEventListener('storage', this.storageHandler);
  }

  disconnectedCallback() {
    this.#clearInterval();
    if (!this.isDesignMode) window.removeEventListener('storage', this.storageHandler);
  }

  #initTimer() {
    if (this.isDesignMode) {
      this.endTimestamp = Date.now() + this.durationMs;
      this.#showActive();
      this.#startInterval();
      return;
    }

    const storedSignature = localStorage.getItem(STORAGE_KEY_SIGNATURE);
    const storedEnd = parseInt(localStorage.getItem(STORAGE_KEY_END), 10);
    const now = Date.now();

    const cartChanged = storedSignature !== this.cartSignature;
    const hasValidStored = !isNaN(storedEnd) && storedEnd > now;

    // ponytail: same product re-added after a clear keeps the same line-key hash, so signature match would re-expire and empty the cart
    if (cartChanged || (this.#isExpired() && this.clearCartOnExpire)) {
      this.#startNewTimer();
      return;
    }

    if (hasValidStored) {
      this.endTimestamp = storedEnd;
      this.#showActive();
      this.#startInterval();
      return;
    }

    this.#handleExpired();
  }

  #isExpired() {
    return localStorage.getItem(STORAGE_KEY_EXPIRED) === 'true';
  }

  #startNewTimer() {
    this.endTimestamp = Date.now() + this.durationMs;
    localStorage.setItem(STORAGE_KEY_END, String(this.endTimestamp));
    localStorage.setItem(STORAGE_KEY_SIGNATURE, this.cartSignature);
    localStorage.removeItem(STORAGE_KEY_EXPIRED);
    this.#showActive();
    this.#startInterval();
  }

  #startInterval() {
    this.#clearInterval();
    this.#updateDisplay();
    this.intervalId = setInterval(() => this.#updateDisplay(), 1000);
  }

  #updateDisplay() {
    const remaining = this.endTimestamp - Date.now();

    if (remaining <= 0) {
      this.#handleExpired();
      return;
    }

    const minutes = Math.floor(remaining / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);
    const formatted = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

    if (this.timeEl) this.timeEl.textContent = formatted;
  }

  #handleExpired() {
    this.#clearInterval();
    localStorage.setItem(STORAGE_KEY_END, '0');
    localStorage.setItem(STORAGE_KEY_EXPIRED, 'true');

    if (this.clearCartOnExpire) {
      localStorage.removeItem(STORAGE_KEY_SIGNATURE);
    } else if (this.cartSignature) {
      localStorage.setItem(STORAGE_KEY_SIGNATURE, this.cartSignature);
    }

    this.#showExpired();

    if (this.clearCartOnExpire) this.#clearCart();
  }

  #setExpired(expired) {
    const wrapper = this.closest('.cart-reservation-timer');
    if (wrapper) wrapper.dataset.expired = expired ? 'true' : 'false';
  }

  #showActive() {
    this.activeEl?.classList.remove('hidden');
    this.emptyEl?.classList.add('hidden');
    this.expiredEl?.classList.add('hidden');
    this.#setExpired(false);
  }

  #showEmpty() {
    this.activeEl?.classList.add('hidden');
    this.emptyEl?.classList.remove('hidden');
    this.expiredEl?.classList.add('hidden');
    this.#setExpired(false);
  }

  #showExpired() {
    this.activeEl?.classList.add('hidden');
    this.emptyEl?.classList.add('hidden');
    this.expiredEl?.classList.remove('hidden');
    this.#setExpired(true);
  }

  async #clearCart() {
    if (this.isDesignMode || this.isClearingCart) return;

    this.isClearingCart = true;

    const sections = ['auto-gifts'];
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer?.dataset.sectionId) sections.push(cartDrawer.dataset.sectionId);

    // ponytail: this theme stores cart.js on Theme.routes.cart_url; strip .js so /cart/clear.js stays valid
    const cartBaseUrl = Theme.routes.cart_url.replace(/\.js$/, '');

    try {
      const response = await fetch(`${cartBaseUrl}/clear.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          sections: sections.join(','),
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok) return;

      const data = await response.json();

      if (window.location.pathname.includes('/cart')) {
        window.location.reload();
        return;
      }

      document.dispatchEvent(
        new CartUpdatedEvent(
          {},
          {
            itemCount: data.item_count ?? 0,
            source: 'cart-reservation-timer',
            sections: data.sections,
          },
        ),
      );

      const wrapper = this.closest('.cart-reservation-timer');
      if (wrapper) wrapper.dataset.cartEmpty = 'true';
    } catch (error) {
      console.error(error);
    } finally {
      this.isClearingCart = false;
    }
  }

  #clearInterval() {
    if (!this.intervalId) return;
    clearInterval(this.intervalId);
    this.intervalId = null;
  }

  #handleStorageChange(event) {
    if (event.key !== STORAGE_KEY_END && event.key !== STORAGE_KEY_SIGNATURE && event.key !== STORAGE_KEY_EXPIRED) {
      return;
    }

    if (!this.cartSignature) {
      if (this.#isExpired()) this.#showExpired();
      else this.#showEmpty();
      return;
    }

    this.#initTimer();
  }
}

if (!customElements.get('cart-reservation-timer')) {
  customElements.define('cart-reservation-timer', CartReservationTimer);
}
